'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Keyboard, Target, GraduationCap, Newspaper, Info, Baby } from 'lucide-react';

const NAV_ITEMS = [
  { href: '/', label: 'Typing Test', icon: Keyboard },
  { href: '/typing-practice', label: 'Practice', icon: Target },
  { href: '/typing-lessons', label: 'Lessons', icon: GraduationCap },
  { href: '/kids', label: 'For Kids', icon: Baby },
  { href: '/blog', label: 'Blog', icon: Newspaper },
  { href: '/about-how-it-works', label: 'How It Works', icon: Info },
];

/** Serif wordmark — weight and italic carry the split, not colour. */
function Wordmark() {
  return (
    <span className="font-serif text-[1.0625rem] leading-none tracking-tight text-foreground">
      <span style={{ fontWeight: 600 }}>Type</span>
      <span style={{ fontWeight: 400, fontStyle: 'italic' }}>Swift</span>
    </span>
  );
}

export default function Topbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 w-full"
      style={{
        backgroundColor: 'var(--background)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Mark + wordmark */}
          <Link href="/" className="flex items-center gap-2.5" aria-label="TypeSwift home">
            <AppLogo size={26} />
            <Wordmark />
          </Link>

          {/* Desktop nav — underline marks the active route */}
          <nav className="hidden lg:flex items-center" aria-label="Main">
            {NAV_ITEMS.map((item) => {
              const isActive =
                pathname === item.href || (item.href !== '/' && pathname?.startsWith(item.href));
              return (
                <Link
                  key={`nav-${item.href}`}
                  href={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              href="/govt-typing-test"
              className="hidden lg:inline-flex text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Exam mode
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden btn-ghost p-2"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={19} /> : <Menu size={19} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden"
          style={{ borderTop: '1px solid var(--border)', backgroundColor: 'var(--background)' }}
        >
          <nav className="flex flex-col px-6 py-2" aria-label="Mobile">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive =
                pathname === item.href || (item.href !== '/' && pathname?.startsWith(item.href));
              return (
                <Link
                  key={`mobile-nav-${item.href}`}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  aria-current={isActive ? 'page' : undefined}
                  className="flex items-center gap-3 py-3 text-sm font-medium transition-colors"
                  style={{
                    borderBottom: '1px solid var(--border)',
                    color: isActive ? 'var(--foreground)' : 'var(--muted-foreground)',
                  }}
                >
                  <Icon size={15} />
                  {item.label}
                </Link>
              );
            })}
            <Link
              href="/govt-typing-test"
              onClick={() => setMobileOpen(false)}
              className="btn-secondary flex items-center justify-center gap-2 px-4 py-2.5 text-sm my-4"
            >
              <GraduationCap size={15} />
              Government exam mode
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
